import React from 'react';
import { DebtAnswer as Answer, DEBT_TYPE_LABEL } from '../services/api';
import { brlOrMasked } from '../utils/format';
import './Debts.css';

/**
 * As respostas do campo de Dívidas.
 *
 * Todo número aqui veio calculado do servidor — nenhum passou pelo modelo.
 * Numa conversa sobre quitar dívida, um número inventado com cara de confiável
 * é o erro mais caro que este app pode cometer.
 *
 * Duas coisas que esta tela diz e não deixa de dizer:
 *
 * - **"Falta pagar" não é o valor de quitação.** Parcela embute os juros dos
 *   meses que ainda viriam; quitar hoje custa menos, e quanto menos só o banco
 *   diz. Aparece junto do número, não num rodapé.
 * - **O app não recomenda quitar nem deixar de quitar.** Ele mostra o que ela
 *   tem e o que os números significam.
 */

interface Props {
  answer: Answer;
  hideValues: boolean;
  /** Cria a meta de reserva proposta. */
  onCreateGoal?: (name: string, targetAmount: number, savedAmount: number) => Promise<void>;
  criandoMeta?: boolean;
}

const mesLegivel = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  const nomes = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  return `${nomes[(m || 1) - 1]}/${y}`;
};

const DebtAnswerView: React.FC<Props> = ({ answer, hideValues, onCreateGoal, criandoMeta }) => {
  const fmt = (v: number) => brlOrMasked(v, hideValues);

  if (answer.kind === 'status') {
    return (
      <div className="dbt-resp">
        <div className="dbt-resp-numeros">
          <div>
            <span className="dbt-resp-label">Sai por mês</span>
            <strong>{fmt(answer.monthlyTotal)}</strong>
          </div>
          <div>
            <span className="dbt-resp-label">Falta pagar</span>
            <strong>{fmt(answer.remainingTotal)}</strong>
          </div>
        </div>
        <ul className="dbt-resp-lista">
          {answer.debts.map(d => (
            <li key={d.id}>
              <strong>{d.name}</strong>: {d.paidInstallments} de {d.installments} pagas,
              falta {fmt(d.remainingTotal)} até {mesLegivel(d.lastInstallmentMonth)}
              {d.porDiaTexto && <span className="dbt-por-dia">{d.porDiaTexto}</span>}
            </li>
          ))}
        </ul>
        {answer.nextRelief && (
          <p className="dbt-resp-nota">
            A primeira folga é em <strong>{mesLegivel(answer.nextRelief.month)}</strong>, quando
            {' '}{answer.nextRelief.name} termina: {fmt(answer.nextRelief.monthlyGain)} a mais
            por mês a partir do mês seguinte.
          </p>
        )}
        <p className="dbt-resp-aviso">
          "Falta pagar" é a soma das parcelas que ainda vão sair, com os juros de cada mês
          embutidos. <strong>Não é o valor de quitação</strong>: quitar hoje custa menos, e
          quanto menos só o banco diz.
        </p>
      </div>
    );
  }

  if (answer.kind === 'where') {
    const d = answer.debt;
    return (
      <div className="dbt-resp">
        <p className="dbt-resp-frase">
          {answer.deductedFromSalary ? (
            <>A parcela de <strong>{d.name}</strong> ({fmt(d.installmentValue)}) é descontada
            direto do seu salário. <strong>Você não lança nada</strong>, porque o salário que
            você registra já vem sem ela.</>
          ) : (
            <>A parcela de <strong>{d.name}</strong> ({fmt(d.installmentValue)}) sai da sua conta.
            <strong> Lance normalmente</strong>, como qualquer outro gasto.</>
          )}
        </p>
        {answer.suspectedDoubleEntry && (
          <p className="dbt-resp-alerta">
            <strong>Atenção:</strong> achei um lançamento deste mês com exatamente
            {' '}{fmt(d.installmentValue)}. Se for esta parcela, ela está sendo contada duas
            vezes (uma pelo salário já líquido, outra pelo lançamento) e seu caixa aparece
            menor do que é.
          </p>
        )}
        <p className="dbt-resp-nota">
          {DEBT_TYPE_LABEL[d.type]} · {d.remainingInstallments} parcelas restantes,
          até {mesLegivel(d.lastInstallmentMonth)}
        </p>
        {d.porDiaTexto && <p className="dbt-resp-nota">{d.porDiaTexto}</p>}
      </div>
    );
  }

  if (answer.kind === 'capacity') {
    return (
      <div className="dbt-resp">
        <div className="dbt-resp-numeros">
          <div>
            <span className="dbt-resp-label">Sobra este mês</span>
            <strong className={answer.monthlyRoom <= 0 ? 'negativo' : ''}>
              {fmt(answer.monthlyRoom)}
            </strong>
          </div>
          {answer.target && (
            <div>
              <span className="dbt-resp-label">Parcela de {answer.target.name}</span>
              <strong>{fmt(answer.target.installmentValue)}</strong>
            </div>
          )}
        </div>

        {answer.monthlyRoom <= 0 ? (
          <p className="dbt-resp-frase">
            Este mês não sobra nada pela conta de "quanto posso gastar": o que entra já está
            comprometido.
          </p>
        ) : answer.installmentsCovered != null && answer.target ? (
          <p className="dbt-resp-frase">
            A sobra deste mês cobre <strong>{answer.installmentsCovered}
            {' '}{answer.installmentsCovered === 1 ? 'parcela' : 'parcelas'}</strong> de
            {' '}{answer.target.name}.
          </p>
        ) : (
          <p className="dbt-resp-frase">
            Diga qual dívida para eu comparar com a parcela dela.
          </p>
        )}

        <p className="dbt-resp-aviso">
          Adiantar parcela normalmente sai <strong>mais barato</strong> do que o valor cheio
          dela, porque os juros dos meses adiantados caem, mas quanto cai só o banco calcula,
          e este app não tem esse número. E a decisão de adiantar ou não é sua: eu mostro os
          seus números, não recomendo o que fazer com eles.
        </p>
      </div>
    );
  }

  // goal
  const completa = answer.targetAmount != null && answer.monthlyAmount != null;
  return (
    <div className="dbt-resp">
      <p className="dbt-resp-frase">
        {completa ? (
          <>Guardando <strong>{fmt(answer.monthlyAmount!)}</strong> por mês, você chega
          em {fmt(answer.targetAmount!)} em <strong>{answer.monthsToTarget} meses</strong>.</>
        ) : answer.missing.includes('targetAmount') ? (
          <>Quanto você quer juntar no total?</>
        ) : (
          <>Quanto você quer guardar por mês?</>
        )}
      </p>

      {completa && onCreateGoal && (
        <button
          className="dbt-resp-acao"
          disabled={criandoMeta}
          aria-busy={criandoMeta}
          onClick={() => onCreateGoal(answer.name, answer.targetAmount!, 0)}
        >
          {`Criar a reserva "${answer.name}"`}
        </button>
      )}

      <p className="dbt-resp-aviso">
        Vira uma reserva comum, em Metas: o dinheiro fica seu e você decide se usa para
        amortizar. Eu não sugiro quanto guardar: esse número é seu.
      </p>
    </div>
  );
};

export default DebtAnswerView;
